import androidx.compose.foundation.Canvas
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.max
import kotlin.math.sin
import kotlin.random.Random

@Composable
fun FireworksStreamerBackground(modifier: Modifier = Modifier) {
    val bursts = remember {
        val random = Random(1776)
        List(BURST_COUNT) { index ->
            val palette = FIREWORK_COLORS[index % FIREWORK_COLORS.size]
            FireworkBurst(
                x = 0.08f + random.nextFloat() * 0.84f,
                y = 0.08f + random.nextFloat() * 0.58f,
                maxRadius = 0.10f + random.nextFloat() * 0.10f,
                cycleOffsetSeconds = index * (BURST_CYCLE_SECONDS / BURST_COUNT) + random.nextFloat() * 0.55f,
                launchSeconds = 0.82f + random.nextFloat() * 0.34f,
                burstSeconds = 2.25f + random.nextFloat() * 0.65f,
                spokes = 16 + random.nextInt(12),
                angleOffset = random.nextFloat() * TWO_PI,
                primary = palette.primary,
                secondary = palette.secondary,
                depth = 0.62f + random.nextFloat() * 0.32f,
            )
        }
    }
    val sparks = remember {
        val random = Random(1812)
        List(AMBIENT_SPARK_COUNT) {
            AmbientSpark(
                x = random.nextFloat(),
                y = random.nextFloat() * 0.82f,
                phase = random.nextFloat() * TWO_PI,
                radius = 1.2f + random.nextFloat() * 2.3f,
                color = FIREWORK_COLORS[random.nextInt(FIREWORK_COLORS.size)].primary,
            )
        }
    }
    var elapsedSeconds by remember { mutableStateOf(0f) }

    LaunchedEffect(Unit) {
        var lastFrame = 0L
        while (true) {
            withFrameNanos { now ->
                if (lastFrame != 0L && now - lastFrame >= FRAME_NANOS) {
                    elapsedSeconds = (elapsedSeconds + ((now - lastFrame) / 1_000_000_000f)) % LOOP_SECONDS
                    lastFrame = now
                } else if (lastFrame == 0L) {
                    lastFrame = now
                }
            }
        }
    }

    Canvas(modifier = modifier) {
        val width = size.width
        val height = size.height
        if (width <= 0f || height <= 0f) return@Canvas

        val scale = size.minDimension
        bursts.forEach { burst ->
            drawFirework(burst, elapsedSeconds, width, height, scale)
        }
        sparks.forEach { spark ->
            drawAmbientSpark(spark, elapsedSeconds, width, height)
        }
    }
}

private fun DrawScope.drawFirework(
    burst: FireworkBurst,
    elapsedSeconds: Float,
    width: Float,
    height: Float,
    scale: Float,
) {
    val local = positiveModulo(elapsedSeconds + burst.cycleOffsetSeconds, BURST_CYCLE_SECONDS)
    val center = Offset(burst.x * width, burst.y * height)
    val launchStart = Offset(
        x = center.x + sin(burst.angleOffset) * width * 0.08f,
        y = height * (0.98f + (burst.angleOffset % 0.2f)),
    )

    if (local < burst.launchSeconds) {
        drawLaunchTrail(
            start = launchStart,
            end = center,
            progress = local / burst.launchSeconds,
            color = burst.primary,
        )
        return
    }

    val burstTime = local - burst.launchSeconds
    if (burstTime > burst.burstSeconds) return

    val t = (burstTime / burst.burstSeconds).coerceIn(0f, 1f)
    val expansion = easeOutCubic(t)
    val fade = (1f - t).coerceIn(0f, 1f)
    val bloom = sin(t * PI.toFloat()).coerceAtLeast(0f)
    val radius = burst.maxRadius * scale * expansion
    val maxRadius = burst.maxRadius * scale

    drawCircle(
        color = burst.primary.copy(alpha = 0.08f * fade * burst.depth),
        radius = radius * 0.72f,
        center = center,
        style = Stroke(width = (1.1f + bloom * 1.4f) * burst.depth),
        blendMode = BlendMode.Plus,
    )
    if (t < 0.18f) {
        val ignition = 1f - t / 0.18f
        drawLine(
            color = burst.secondary.copy(alpha = 0.3f * ignition * burst.depth),
            start = Offset(center.x - maxRadius * 0.035f, center.y),
            end = Offset(center.x + maxRadius * 0.035f, center.y),
            strokeWidth = 1.4f,
            cap = StrokeCap.Round,
            blendMode = BlendMode.Plus,
        )
        drawLine(
            color = burst.secondary.copy(alpha = 0.3f * ignition * burst.depth),
            start = Offset(center.x, center.y - maxRadius * 0.035f),
            end = Offset(center.x, center.y + maxRadius * 0.035f),
            strokeWidth = 1.4f,
            cap = StrokeCap.Round,
            blendMode = BlendMode.Plus,
        )
    }

    for (spoke in 0 until burst.spokes) {
        val fraction = spoke / burst.spokes.toFloat()
        val angle = fraction * TWO_PI + burst.angleOffset + sin(t * TWO_PI + spoke) * 0.035f
        val variance = 0.76f + 0.24f * sin(spoke * 12.9898f + burst.angleOffset).coerceIn(-1f, 1f)
        val distance = radius * variance
        val gravity = maxRadius * 0.22f * t * t
        val tailLength = maxRadius * (0.08f + (spoke % 4) * 0.012f) * (1f - t * 0.45f)
        val end = Offset(
            x = center.x + cos(angle) * distance,
            y = center.y + sin(angle) * distance + gravity,
        )
        val start = Offset(
            x = center.x + cos(angle) * max(0f, distance - tailLength),
            y = center.y + sin(angle) * max(0f, distance - tailLength) + gravity * 0.82f,
        )
        val particleColor = when (spoke % 5) {
            0 -> burst.secondary
            1 -> burst.secondary
            else -> burst.primary
        }
        drawLine(
            color = particleColor.copy(alpha = 0.075f * fade * bloom * burst.depth),
            start = start,
            end = end,
            strokeWidth = (3.2f + bloom * 2.2f) * burst.depth,
            cap = StrokeCap.Round,
            blendMode = BlendMode.Plus,
        )
        drawLine(
            color = particleColor.copy(alpha = 0.26f * fade * burst.depth),
            start = start,
            end = end,
            strokeWidth = (1.05f + bloom * 0.8f) * burst.depth,
            cap = StrokeCap.Round,
            blendMode = BlendMode.Plus,
        )
        if (spoke % 6 == 0 && t in 0.18f..0.74f) {
            val sparkle = sin(t * TWO_PI * 2f + spoke).coerceAtLeast(0f) * fade
            val arm = (2.6f + bloom * 2f) * burst.depth
            drawLine(
                color = particleColor.copy(alpha = 0.24f * sparkle * burst.depth),
                start = Offset(end.x - arm, end.y),
                end = Offset(end.x + arm, end.y),
                strokeWidth = 0.9f,
                cap = StrokeCap.Round,
                blendMode = BlendMode.Plus,
            )
            drawLine(
                color = particleColor.copy(alpha = 0.24f * sparkle * burst.depth),
                start = Offset(end.x, end.y - arm),
                end = Offset(end.x, end.y + arm),
                strokeWidth = 0.9f,
                cap = StrokeCap.Round,
                blendMode = BlendMode.Plus,
            )
        }
    }
}

private fun DrawScope.drawLaunchTrail(
    start: Offset,
    end: Offset,
    progress: Float,
    color: Color,
) {
    val t = easeOutCubic(progress.coerceIn(0f, 1f))
    val wobble = sin(progress * PI.toFloat() * 2f) * 12f * (1f - progress)
    val rocket = Offset(
        x = start.x + (end.x - start.x) * t + wobble,
        y = start.y + (end.y - start.y) * t,
    )
    val tail = Offset(
        x = rocket.x - wobble * 0.45f,
        y = rocket.y + size.height * 0.07f * (1f - progress * 0.45f),
    )
    val alpha = 0.16f + 0.14f * (1f - progress)
    drawLine(
        color = color.copy(alpha = alpha * 0.08f),
        start = Offset(tail.x, tail.y + size.height * 0.026f),
        end = rocket,
        strokeWidth = 5.5f,
        cap = StrokeCap.Round,
        blendMode = BlendMode.Plus,
    )
    drawLine(
        color = color.copy(alpha = alpha),
        start = tail,
        end = rocket,
        strokeWidth = 1.7f,
        cap = StrokeCap.Round,
        blendMode = BlendMode.Plus,
    )
    drawLine(
        color = Color.White.copy(alpha = alpha * 0.35f),
        start = Offset(rocket.x - 2.4f, rocket.y),
        end = Offset(rocket.x + 2.4f, rocket.y),
        strokeWidth = 0.9f,
        cap = StrokeCap.Round,
        blendMode = BlendMode.Plus,
    )
}

private fun DrawScope.drawAmbientSpark(
    spark: AmbientSpark,
    elapsedSeconds: Float,
    width: Float,
    height: Float,
) {
    val pulse = 0.5f + 0.5f * sin(elapsedSeconds * 1.4f + spark.phase)
    val x = spark.x * width + sin(elapsedSeconds * 0.12f + spark.phase) * width * 0.012f
    val y = spark.y * height + cos(elapsedSeconds * 0.1f + spark.phase) * height * 0.008f
    val center = Offset(x, y)
    val alpha = 0.045f + pulse * 0.105f
    val arm = spark.radius * (1.6f + pulse * 0.9f)
    drawLine(
        color = spark.color.copy(alpha = alpha),
        start = Offset(center.x - arm, center.y),
        end = Offset(center.x + arm, center.y),
        strokeWidth = 0.85f,
        cap = StrokeCap.Round,
        blendMode = BlendMode.Plus,
    )
    drawLine(
        color = spark.color.copy(alpha = alpha * 0.76f),
        start = Offset(center.x, center.y - arm),
        end = Offset(center.x, center.y + arm),
        strokeWidth = 0.85f,
        cap = StrokeCap.Round,
        blendMode = BlendMode.Plus,
    )
    if (pulse > 0.82f) {
        val diagonalArm = arm * 0.72f
        drawLine(
            color = spark.color.copy(alpha = (pulse - 0.82f) * 0.34f),
            start = Offset(center.x - diagonalArm, center.y - diagonalArm),
            end = Offset(center.x + diagonalArm, center.y + diagonalArm),
            strokeWidth = 0.7f,
            cap = StrokeCap.Round,
            blendMode = BlendMode.Plus,
        )
        drawLine(
            color = spark.color.copy(alpha = (pulse - 0.82f) * 0.34f),
            start = Offset(center.x - diagonalArm, center.y + diagonalArm),
            end = Offset(center.x + diagonalArm, center.y - diagonalArm),
            strokeWidth = 0.7f,
            cap = StrokeCap.Round,
            blendMode = BlendMode.Plus,
        )
    }
}

private fun easeOutCubic(value: Float): Float {
    val inverse = 1f - value.coerceIn(0f, 1f)
    return 1f - inverse * inverse * inverse
}

private fun positiveModulo(value: Float, modulus: Float): Float {
    val result = value % modulus
    return if (result < 0f) result + modulus else result
}

private data class FireworkBurst(
    val x: Float,
    val y: Float,
    val maxRadius: Float,
    val cycleOffsetSeconds: Float,
    val launchSeconds: Float,
    val burstSeconds: Float,
    val spokes: Int,
    val angleOffset: Float,
    val primary: Color,
    val secondary: Color,
    val depth: Float,
)

private data class AmbientSpark(
    val x: Float,
    val y: Float,
    val phase: Float,
    val radius: Float,
    val color: Color,
)

private data class FireworkPalette(
    val primary: Color,
    val secondary: Color,
)

private val FIREWORK_COLORS = listOf(
    FireworkPalette(Color(0xFFE53E3E), Color(0xFFFFD166)),
    FireworkPalette(Color(0xFF2563EB), Color(0xFFDBEAFE)),
    FireworkPalette(Color(0xFFFFFFFF), Color(0xFF93C5FD)),
    FireworkPalette(Color(0xFFFFD166), Color(0xFFE53E3E)),
)

private const val BURST_COUNT = 14
private const val AMBIENT_SPARK_COUNT = 42
private const val BURST_CYCLE_SECONDS = 8.2f
private const val FRAME_NANOS = 33_000_000L
private const val LOOP_SECONDS = 60f
private const val TWO_PI = (PI * 2).toFloat()
